/// <reference lib="dom" />
/** @jsx h */
import { h } from "preact";
import { useEffect } from "preact/hooks";
import { clamp } from "@utils/utils";
import type { ChartMode } from "../../core/mode.ts";
import { useChart, useLatest } from "../../core/useChart.ts";
import { BandLayer } from "../../layers/bands.ts";
import { MarkerLayer } from "../../layers/markers.ts";
import { type HandlePosition, HandlesLayer } from "../../layers/handles.ts";
import { FormatHz } from "../../utils/format.ts";
import { ChartPalette } from "../../utils/theme.ts";
import { CreateFrequencyChart, FrequencyDisplay } from "./frequencyChart.ts";

export interface TelephonizerBandParam {
  value: number;
  min: number;
  max: number;
}

export interface TelephonizerCurveProps {
  highpass: TelephonizerBandParam;
  lowpass: TelephonizerBandParam;
  onChange?: (value: { highpass: number; lowpass: number }) => void;
}

function CreateTelephonizerChart(
  container: HTMLElement,
  mode: ChartMode,
  onDrag: (id: string, position: HandlePosition) => void,
) {
  const band = BandLayer();
  const edges = mode === "inline" ? MarkerLayer() : null;
  const handles = mode === "editor" ? HandlesLayer({ onDrag }) : null;
  const chart = CreateFrequencyChart(container, {
    mode,
    layers: [band, ...(edges ? [edges] : []), ...(handles ? [handles] : [])],
  });
  return { chart, band, edges, handles, destroy: chart.destroy };
}

export function TelephonizerCurve(props: TelephonizerCurveProps) {
  const mode: ChartMode = props.onChange ? "editor" : "inline";
  const latest = useLatest(props);
  const { ref, chart } = useChart(
    (container) =>
      CreateTelephonizerChart(container, mode, (id, p) => {
        const { highpass, lowpass, onChange } = latest.current;
        const freq = Math.round(p.x);
        onChange?.({
          highpass: id === "highpass" ? clamp(freq, highpass.min, highpass.max) : highpass.value,
          lowpass: id === "lowpass" ? clamp(freq, lowpass.min, lowpass.max) : lowpass.value,
        });
      }),
    [mode],
  );

  const hp = props.highpass.value;
  const lp = props.lowpass.value;

  useEffect(() => {
    if (!chart) return;
    const { fMin, fMax } = FrequencyDisplay;
    const hpHz = clamp(hp, fMin, fMax);
    const lpHz = clamp(lp, fMin, fMax);
    chart.band.set([{ id: "band", x0: hpHz, x1: lpHz, color: ChartPalette.blue }]);
    chart.edges?.set([
      { value: hpHz, color: ChartPalette.blue, opacity: 0.7 },
      { value: lpHz, color: ChartPalette.orange, opacity: 0.7 },
    ]);
    chart.handles?.set([
      {
        id: "highpass",
        x: hpHz,
        y: 0.5,
        axis: "x",
        guide: "x",
        color: ChartPalette.blue,
        label: FormatHz(hp),
      },
      {
        id: "lowpass",
        x: lpHz,
        y: 0.5,
        axis: "x",
        guide: "x",
        color: ChartPalette.orange,
        label: FormatHz(lp),
      },
    ]);
    chart.chart.render();
  }, [chart, hp, lp]);

  return <div ref={ref} />;
}
